import { useEffect, useState, useSyncExternalStore } from "react";
import type { BoardDocument, BoardTable } from "../shared/schema";
import type { WorkspaceModel } from "./useWorkspace";
import { checkedRows } from "../shared/file-content";
import {
  applyCellStyle,
  deleteTableColumn,
  deleteTableRow,
  displayTableRows,
  insertTableColumn,
  insertTableRow,
  moveTableColumn,
  moveTableRow,
  normalizeCellRange,
  savedTableView,
  sortTableModel,
  tableColumnWidths,
  tableObjectHeight,
  tableRowHeights,
  type CellStyle,
  type TableMeta,
  type TableModel,
} from "../shared/table-tools";
import {
  getTableInteraction,
  selectTableCell,
  setTableSearch,
  subscribeTableInteraction,
  tableSelection,
} from "./table-interaction";
import { notify } from "./api";
import { selectedTableStyle } from "./table-style-state";
import { documentLineHeight } from "./document-format";
export function EditableDocumentControls({
  model,
  shape,
}: {
  model: WorkspaceModel;
  shape: BoardDocument;
}) {
  const editor = model.editor;
  const editable = model.canEdit && !editor.getIsReadonly() && !shape.isLocked;
  return (
    <section className="object-tools" aria-label="Document tools">
      <h3 className="calque-inspector-heading">Typography</h3>
      <label>
        Document font size
        <input
          aria-label="Document font size"
          type="number"
          min={10}
          max={72}
          value={shape.props.fontSize}
          disabled={!editable}
          onChange={(event) => {
            const value = Number(event.target.value);
            if (!Number.isFinite(value) || value < 10 || value > 72) return;
            editor.markHistoryStoppingPoint("Resize document text");
            editor.updateShape<BoardDocument>({
              id: shape.id,
              type: "document",
              props: { fontSize: value },
            });
          }}
        />
      </label>
      <label>
        Document line spacing
        <input
          aria-label="Document line spacing"
          type="number"
          min={1}
          max={2.5}
          step={0.05}
          value={documentLineHeight(shape)}
          disabled={!editable}
          onChange={(event) => {
            const value = Number(event.target.value);
            if (!Number.isFinite(value) || value < 1 || value > 2.5) return;
            editor.markHistoryStoppingPoint("Set document line spacing");
            editor.updateShape<BoardDocument>({
              id: shape.id,
              type: "document",
              meta: { ...shape.meta, calqueLineHeight: value },
            });
          }}
        />
      </label>
    </section>
  );
}
export function TableControls({
  model,
  shape,
}: {
  model: WorkspaceModel;
  shape: BoardTable;
}) {
  const editable =
    model.canEdit && !model.editor.getIsReadonly() && !shape.isLocked;
  const interaction = useSyncExternalStore(
      (listener) => subscribeTableInteraction(shape.id, listener),
      () => getTableInteraction(shape.id),
      () => getTableInteraction(shape.id),
    ),
    range = normalizeCellRange(tableSelection(shape.id), shape.props.rows),
    active = normalizeCellRange(
      {
        startRow: interaction.focus.row,
        endRow: interaction.focus.row,
        startColumn: interaction.focus.column,
        endColumn: interaction.focus.column,
      },
      shape.props.rows,
    ),
    r = active.endRow,
    c = active.endColumn,
    savedView = savedTableView(shape.meta, shape.props.rows),
    [viewDraft, setViewDraft] = useState(savedView),
    [csvScope, setCsvScope] = useState<"all" | "view">("all"),
    [pendingDelete, setPendingDelete] = useState<"row" | "column" | null>(null);
  useEffect(
    () => setViewDraft(savedView),
    [shape.id, JSON.stringify(savedView)],
  );
  useEffect(() => setPendingDelete(null), [shape.id]);

  const update = (
    rows: string[][],
    meta: TableMeta,
    label = "Change table",
  ) => {
    const current = model.editor.getShape<BoardTable>(shape.id);
    if (!current || current.isLocked || model.editor.getIsReadonly()) return;
    try {
      rows = checkedRows(rows);
      const grid = current.props.layout === "grid";
      model.editor.markHistoryStoppingPoint(label);
      model.editor.updateShape<BoardTable>({
        id: shape.id,
        type: "table",
        props: {
          rows,
          w: Array.isArray(meta.columnWidths)
            ? Math.max(
                240,
                tableColumnWidths(meta, rows[0].length).reduce(
                  (sum, width) => sum + width,
                  0,
                ),
              )
            : current.props.w,
          h: tableObjectHeight(rows, meta, grid),
        },
        meta: meta as typeof current.meta,
      });
    } catch (e) {
      notify((e as Error).message);
    }
  };
  const change = (
    fn: (rows: string[][], meta: TableMeta) => TableModel,
    label?: string,
  ) => {
    const current = model.editor.getShape<BoardTable>(shape.id);
    if (!current) return;
    const next = fn(
      current.props.rows.map((row) => [...row]),
      { ...current.meta },
    );
    update(next.rows, next.meta, label);
  };
  const style = (patch: Partial<CellStyle>) => {
    const current = model.editor.getShape<BoardTable>(shape.id);
    if (!current) return;
    try {
      update(
        current.props.rows,
        applyCellStyle(current.meta, current.props.rows, range, patch),
        "Style table cells",
      );
    } catch (error) {
      notify((error as Error).message);
    }
  };
  const selectedStyle = selectedTableStyle(shape.meta, shape.props.rows, range),
    columns = shape.props.rows[0].length,
    widths = tableColumnWidths(shape.meta, columns),
    heights = tableRowHeights(
      shape.meta,
      shape.props.rows,
      shape.props.layout === "grid",
    );
  return (
    <section className="object-tools" aria-label="Table tools">
      <fieldset className="table-controls-fieldset" disabled={!editable}>
        <div className="table-inspector-context">
          <span>
            {shape.props.rows.length} rows × {columns} columns
          </span>
          <span
            className="table-range-summary"
            title={`Active R${r + 1}:C${c + 1}`}
          >
            R{range.startRow + 1}:C{range.startColumn + 1}
            {(range.startRow !== range.endRow ||
              range.startColumn !== range.endColumn) &&
              `–R${range.endRow + 1}:C${range.endColumn + 1}`}
          </span>
        </div>
        <details className="table-group" open>
          <summary className="table-group-name">Cell style</summary>
          <div className="table-style-fields">
            <label>
              Font
              <select
                aria-label="Table font"
                value={selectedStyle.fontFamily ?? ""}
                onChange={(e) =>
                  style({
                    fontFamily: e.target.value as CellStyle["fontFamily"],
                  })
                }
              >
                <option value="" disabled>
                  Mixed
                </option>
                <option>Arial</option>
                <option>Georgia</option>
                <option>monospace</option>
              </select>
            </label>
            <label>
              Font size
              <input
                aria-label="Selected table font size"
                type="number"
                min={10}
                max={32}
                value={selectedStyle.fontSize ?? ""}
                placeholder={
                  selectedStyle.fontSize === null ? "Mixed" : undefined
                }
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value >= 10 && value <= 32) style({ fontSize: value });
                }}
              />
            </label>
            <label className="check">
              <input
                type="checkbox"
                ref={(input) => {
                  if (input)
                    input.indeterminate = selectedStyle.italic === null;
                }}
                aria-checked={
                  selectedStyle.italic === null ? "mixed" : selectedStyle.italic
                }
                checked={!!selectedStyle.italic}
                onChange={(e) => style({ italic: e.target.checked })}
              />
              Italic cells
            </label>
            <label>
              Alignment
              <select
                value={selectedStyle.align ?? ""}
                onChange={(e) =>
                  style({ align: e.target.value as CellStyle["align"] })
                }
              >
                <option value="" disabled>
                  Mixed
                </option>
                <option>left</option>
                <option>center</option>
                <option>right</option>
              </select>
            </label>
            <label className="check">
              <input
                type="checkbox"
                ref={(input) => {
                  if (input) input.indeterminate = selectedStyle.bold === null;
                }}
                aria-checked={
                  selectedStyle.bold === null ? "mixed" : selectedStyle.bold
                }
                checked={!!selectedStyle.bold}
                onChange={(e) => style({ bold: e.target.checked })}
              />
              Bold cells
            </label>
            {[
              ["textColor", "Text", "#25343b"],
              ["fill", "Fill", "#ffffff"],
              ["border", "Border", "#d6dad3"],
            ].map(([key, label, fallback]) => (
              <label key={key}>
                {label}
                {selectedStyle[key as keyof CellStyle] === null
                  ? " · Mixed"
                  : ""}
                <input
                  aria-label={`Table ${label.toLowerCase()} color`}
                  type="color"
                  value={String(
                    selectedStyle[key as keyof CellStyle] || fallback,
                  )}
                  onChange={(e) => style({ [key]: e.target.value })}
                />
              </label>
            ))}
            <button
              onClick={() =>
                style({
                  fontFamily: undefined,
                  fontSize: undefined,
                  textColor: undefined,
                  fill: undefined,
                  border: undefined,
                  align: undefined,
                  bold: undefined,
                  italic: undefined,
                })
              }
            >
              Clear selected style
            </button>
          </div>
        </details>
        <details className="table-group">
          <summary className="table-group-name">Rows &amp; columns</summary>
          <div className="table-subsection">
            <h4>Selection</h4>
            <button
              onClick={() => {
                selectTableCell(shape.id, 0, 0);
                selectTableCell(
                  shape.id,
                  shape.props.rows.length - 1,
                  columns - 1,
                  true,
                );
              }}
            >
              Select whole table
            </button>
            <div className="tool-fields">
              <label>
                Row
                <input
                  aria-label="Table row"
                  type="number"
                  min={1}
                  max={shape.props.rows.length}
                  value={r + 1}
                  onChange={(e) =>
                    selectTableCell(
                      shape.id,
                      Math.max(0, Number(e.target.value) - 1),
                      c,
                    )
                  }
                />
              </label>
              <label>
                Column
                <input
                  aria-label="Table column"
                  type="number"
                  min={1}
                  max={shape.props.rows[0].length}
                  value={c + 1}
                  onChange={(e) =>
                    selectTableCell(
                      shape.id,
                      r,
                      Math.max(0, Number(e.target.value) - 1),
                    )
                  }
                />
              </label>
            </div>
            <div className="format-buttons">
              <button
                onClick={() =>
                  change(
                    (rows, meta) => insertTableRow(rows, meta, r + 1),
                    "Add table row",
                  )
                }
              >
                Add row
              </button>
              <button
                onClick={() =>
                  change(
                    (rows, meta) => insertTableColumn(rows, meta, c + 1),
                    "Add table column",
                  )
                }
              >
                Add column
              </button>
              <button
                disabled={shape.props.rows.length <= 1}
                onClick={() => setPendingDelete("row")}
              >
                Delete row…
              </button>
              <button
                disabled={shape.props.rows[0].length <= 1}
                onClick={() => setPendingDelete("column")}
              >
                Delete column…
              </button>
              <button
                disabled={r <= 0}
                onClick={() =>
                  change(
                    (rows, meta) => moveTableRow(rows, meta, r, r - 1),
                    "Move table row",
                  )
                }
              >
                Row up
              </button>
              <button
                disabled={c <= 0}
                onClick={() =>
                  change(
                    (rows, meta) => moveTableColumn(rows, meta, c, c - 1),
                    "Move table column",
                  )
                }
              >
                Column left
              </button>
              <button
                disabled={r >= shape.props.rows.length - 1}
                onClick={() =>
                  change(
                    (rows, meta) => moveTableRow(rows, meta, r, r + 1),
                    "Move table row",
                  )
                }
              >
                Row down
              </button>
              <button
                disabled={c >= shape.props.rows[0].length - 1}
                onClick={() =>
                  change(
                    (rows, meta) => moveTableColumn(rows, meta, c, c + 1),
                    "Move table column",
                  )
                }
              >
                Column right
              </button>
            </div>
            {pendingDelete && (
              <div role="group" aria-label="Confirm shared table deletion">
                <small>
                  Delete{" "}
                  {pendingDelete === "row" ? `row ${r + 1}` : `column ${c + 1}`}{" "}
                  for everyone? This removes its cells and can be undone.
                </small>
                <div className="format-buttons">
                  <button onClick={() => setPendingDelete(null)}>Cancel</button>
                  <button
                    onClick={() => {
                      if (pendingDelete === "row") {
                        change(
                          (rows, meta) => deleteTableRow(rows, meta, r),
                          "Delete table row",
                        );
                        selectTableCell(shape.id, Math.max(0, r - 1), c);
                      } else {
                        change(
                          (rows, meta) => deleteTableColumn(rows, meta, c),
                          "Delete table column",
                        );
                        selectTableCell(shape.id, r, Math.max(0, c - 1));
                      }
                      setPendingDelete(null);
                    }}
                  >
                    Delete for everyone
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="table-subsection">
            <h4>Sort rows</h4>
            <div className="format-buttons">
              <button
                onClick={() =>
                  change(
                    (rows, meta) =>
                      sortTableModel(rows, meta, c, true, shape.props.headers),
                    "Sort table",
                  )
                }
              >
                Sort A–Z
              </button>
              <button
                onClick={() =>
                  change(
                    (rows, meta) =>
                      sortTableModel(rows, meta, c, false, shape.props.headers),
                    "Sort table",
                  )
                }
              >
                Sort Z–A
              </button>
            </div>
            <small>
              Sorts entire rows by column {c + 1}; headers stay in place.
            </small>
            <small>Sorting changes the shared table.</small>
          </div>
          <div className="table-subsection">
            <h4>Cell dimensions</h4>
            <label>
              <input
                type="checkbox"
                checked={shape.meta.autoFitRows === true}
                onChange={(event) =>
                  update(shape.props.rows, {
                    ...shape.meta,
                    autoFitRows: event.target.checked,
                  })
                }
              />
              Fit rows to wrapped text
            </label>
            <label>
              Column {c + 1} width
              <input
                type="number"
                min={60}
                max={2000}
                value={widths[c]}
                onChange={(event) => {
                  const next = [...widths];
                  next[c] = Math.max(
                    60,
                    Math.min(2000, Number(event.target.value)),
                  );
                  update(shape.props.rows, {
                    ...shape.meta,
                    columnWidths: next,
                  });
                }}
              />
            </label>
            <label>
              Row {r + 1} height
              <input
                type="number"
                min={34}
                max={10000}
                value={heights[r]}
                onChange={(event) => {
                  const next = [...heights];
                  next[r] = Math.max(
                    34,
                    Math.min(10000, Number(event.target.value)),
                  );
                  update(shape.props.rows, { ...shape.meta, rowHeights: next });
                }}
              />
            </label>
            <div className="format-buttons">
              <button
                onClick={() => {
                  const next = [...widths],
                    longest = Math.max(
                      ...shape.props.rows.map((row) => (row[c] || "").length),
                    );
                  next[c] = Math.max(80, Math.min(420, 36 + longest * 8));
                  update(shape.props.rows, {
                    ...shape.meta,
                    columnWidths: next,
                  });
                }}
              >
                Fit column
              </button>
              <button
                onClick={() => {
                  const next = [...heights],
                    lines = Math.max(
                      ...shape.props.rows[r].map(
                        (value) => value.split(/\r?\n/).length,
                      ),
                    );
                  next[r] = Math.max(38, Math.min(10000, 18 + lines * 22));
                  update(shape.props.rows, { ...shape.meta, rowHeights: next });
                }}
              >
                Fit row
              </button>
              <button
                onClick={() =>
                  update(shape.props.rows, {
                    ...shape.meta,
                    columnWidths: Array(columns).fill(160),
                    rowHeights: Array(shape.props.rows.length).fill(
                      shape.props.layout === "grid" ? 72 : 38,
                    ),
                  })
                }
              >
                Reset sizes
              </button>
            </div>
          </div>
        </details>
        <details className="table-group">
          <summary className="table-group-name">View</summary>
          <div className="table-subsection">
            <h4>Find on this device</h4>
            <label>
              Find in table
              <input
                type="search"
                value={interaction.search}
                placeholder="Highlight matching rows"
                onChange={(event) =>
                  setTableSearch(shape.id, event.target.value)
                }
              />
            </label>
            <small>
              Find stays on this device and never changes shared rows.
            </small>
          </div>
          <div className="table-subsection">
            <h4>Layout</h4>
            <label>
              Layout
              <select
                value={shape.props.layout}
                onChange={(e) =>
                  model.editor.updateShape<BoardTable>({
                    id: shape.id,
                    type: "table",
                    props: {
                      layout: e.target.value as "grid" | "table",
                      h: tableObjectHeight(
                        shape.props.rows,
                        shape.meta,
                        e.target.value === "grid",
                      ),
                    },
                  })
                }
              >
                <option value="table">Table</option>
                <option value="grid">Visual grid</option>
              </select>
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={shape.props.headers}
                onChange={(e) =>
                  model.editor.updateShape<BoardTable>({
                    id: shape.id,
                    type: "table",
                    props: { headers: e.target.checked },
                  })
                }
              />
              Header row
            </label>
          </div>
          <div className="table-subsection">
            <h4>Shared filter &amp; grouping</h4>
            <label>
              Filter column
              <select
                value={viewDraft.filterColumn ?? ""}
                onChange={(event) =>
                  setViewDraft({
                    ...viewDraft,
                    filterColumn:
                      event.target.value === ""
                        ? null
                        : Number(event.target.value),
                  })
                }
              >
                <option value="">No filter</option>
                {shape.props.rows[0].map((name, index) => (
                  <option value={index} key={index}>
                    {name || `Column ${index + 1}`}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Contains
              <input
                maxLength={120}
                value={viewDraft.filterValue}
                disabled={viewDraft.filterColumn === null}
                onChange={(event) =>
                  setViewDraft({
                    ...viewDraft,
                    filterValue: event.target.value,
                  })
                }
              />
            </label>
            <label>
              Group column
              <select
                value={viewDraft.groupColumn ?? ""}
                onChange={(event) =>
                  setViewDraft({
                    ...viewDraft,
                    groupColumn:
                      event.target.value === ""
                        ? null
                        : Number(event.target.value),
                  })
                }
              >
                <option value="">No grouping</option>
                {shape.props.rows[0].map((name, index) => (
                  <option value={index} key={index}>
                    {name || `Column ${index + 1}`}
                  </option>
                ))}
              </select>
            </label>
            <div className="format-buttons">
              <button
                onClick={() =>
                  update(shape.props.rows, {
                    ...shape.meta,
                    tableView: viewDraft,
                  })
                }
              >
                Save shared view
              </button>
              <button
                onClick={() => {
                  const empty = {
                    filterColumn: null,
                    filterValue: "",
                    groupColumn: null,
                  };
                  setViewDraft(empty);
                  update(shape.props.rows, { ...shape.meta, tableView: empty });
                }}
              >
                Clear view
              </button>
            </div>
            <small>
              Saved filters and groups are shared and exported. They never
              delete source rows.
            </small>
          </div>
          <div className="table-subsection">
            <h4>Download</h4>
            <label>
              CSV rows
              <select
                value={csvScope}
                onChange={(event) =>
                  setCsvScope(event.target.value as "all" | "view")
                }
              >
                <option value="all">All source rows</option>
                <option value="view">Current saved view</option>
              </select>
            </label>
            <button
              onClick={() => {
                const visible = displayTableRows(
                    shape.props.rows,
                    shape.props.headers,
                    shape.meta,
                  )
                    .filter((entry) => entry.kind === "row")
                    .map((entry) => entry.row),
                  rows =
                    csvScope === "view"
                      ? [
                          ...(shape.props.headers ? [shape.props.rows[0]] : []),
                          ...visible,
                        ]
                      : shape.props.rows,
                  csv = rows
                    .map((r) =>
                      r
                        .map((c) => '"' + c.replaceAll('"', '""') + '"')
                        .join(","),
                    )
                    .join("\r\n");
                const url = URL.createObjectURL(
                  new Blob([csv], { type: "text/csv" }),
                );
                const a = document.createElement("a");
                a.href = url;
                a.download =
                  shape.props.name.replace(/\.[^.]+$/, "") + "-edited.csv";
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 10000);
              }}
            >
              Download edited CSV
            </button>
          </div>
        </details>
      </fieldset>
    </section>
  );
}
